import { ref } from 'vue';

import {
  CHECKISH_STATES,
  type CheckedStateType,
  type ItemStateType,
  type TreeStateType,
  type TreeViewItemType,
  type UseGraphType
} from '#components/treeView';

export function useGraph({ treeItems, onItemsChecked, onItemChecked }: UseGraphType): TreeStateType {
  const childParentLookUpMap = new Map<string, TreeViewItemType>();
  const checkedItemsLookupMap = new Map<string, ItemStateType>();
  treeItems.forEach((node) => treeTraversal(node, childParentLookUpMap, checkedItemsLookupMap));

  const childParentLookUp = ref(childParentLookUpMap);
  const checkedItemsLookup = ref(checkedItemsLookupMap);

  function handleItemsCheckedChange() {
    onItemsChecked(Object.fromEntries(checkedItemsLookup.value.entries()));
  }
  const getParent = (nodeId: string) => childParentLookUp.value.get(nodeId);

  const getNodeCheckState = (nodeId: string): CheckedStateType => {
    return checkedItemsLookup.value.get(nodeId)?.state ?? 'unchecked';
  };
  const setNodeCheckState = (node: TreeViewItemType, checkedState: CheckedStateType) => {
    checkedItemsLookup.value.set(node.nodeId, {
      filterType: node.type,
      state: checkedState,
      label: node.label,
      nodeId: node.nodeId
    });
  };

  const cascadeStateToDescendants = (checkedState: CheckedStateType, item: TreeViewItemType): void => {
    item.children?.forEach((child) => {
      const childCheckState = getChildCheckState(checkedState, child.checkedStatus);
      setNodeCheckState(child, childCheckState);
      cascadeStateToDescendants(checkedState, child);
    });
  };
  const notifySibling = (nodeId: string, parent: TreeViewItemType) => {
    for (const child of parent.children) {
      if (nodeId === child.nodeId) {
        continue;
      }
      const checkState = getChildCheckState(getNodeCheckState(parent.nodeId), getNodeCheckState(child.nodeId));

      setNodeCheckState(child, checkState);
    }
  };

  const notifyParentOfSelection = (child: TreeViewItemType): void => {
    const parent = getParent(child.nodeId);

    if (!parent) {
      return;
    }

    const isEveryChildChecked = parent.children?.every((child) =>
      CHECKISH_STATES.includes(getNodeCheckState(child.nodeId))
    );

    const hasIntermediate = parent.children?.some((child) => getNodeCheckState(child.nodeId) === 'indeterminate');
    const hasAnUncheckedChild = parent.children?.some((child) => getNodeCheckState(child.nodeId) == 'unchecked');
    const hasACheckedChild = parent.children?.some((child) =>
      CHECKISH_STATES.includes(getNodeCheckState(child.nodeId))
    );

    const parentCheckState = getParentCheckState(
      isEveryChildChecked,
      hasIntermediate,
      hasAnUncheckedChild,
      hasACheckedChild
    );
    setNodeCheckState(parent, parentCheckState);
    notifyParentOfSelection(parent);
    notifySibling(child.nodeId, parent);
  };

  const emitItemCheckedChange = (node: TreeViewItemType) => {
    checkedItemsLookup.value.set(node.nodeId, {
      filterType: node.type,
      state: node.checkedStatus,
      label: node.label,
      nodeId: node.nodeId
    });
    onItemChecked({
      itemId: node.nodeId,
      change: {
        filterType: node.type,
        state: node.checkedStatus,
        label: node.label,
        nodeId: node.nodeId
      }
    });

    const parent = getParent(node.nodeId);

    notifyParentOfSelection(node);
    cascadeStateToDescendants(node.checkedStatus, node);
    if (parent) {
      notifySibling(node.nodeId, parent);
    }

    handleItemsCheckedChange();
  };

  return {
    getParent,
    setParent: (nodeId, parentItem) => childParentLookUp.value.set(nodeId, parentItem),
    getNodeCheckState,
    emitItemCheckedChange
  };
}

function treeTraversal(
  treeItem: TreeViewItemType,
  childParentLookUp: Map<string, TreeViewItemType>,
  checkedItemsLookupMap: Map<string, ItemStateType>
): Map<string, TreeViewItemType> {
  checkedItemsLookupMap.set(treeItem.nodeId, {
    filterType: treeItem.type,
    state: treeItem.checkedStatus,
    label: treeItem.label,
    nodeId: treeItem.nodeId
  });

  for (const childNode of treeItem.children ?? []) {
    childParentLookUp.set(childNode.nodeId, treeItem);
    checkedItemsLookupMap.set(childNode.nodeId, {
      filterType: childNode.type,
      state: childNode.checkedStatus,
      label: childNode.label,
      nodeId: treeItem.nodeId
    });
    treeTraversal(childNode, childParentLookUp, checkedItemsLookupMap);
  }
  return childParentLookUp;
}

function getParentCheckState(
  isEveryChildChecked: boolean,
  hasIntermediate: boolean,
  hasAnUncheckedChild: boolean,
  hasACheckedChild: boolean
): CheckedStateType {
  if (isEveryChildChecked || hasIntermediate || (hasAnUncheckedChild && hasACheckedChild)) {
    return 'indeterminate';
  } else {
    return 'unchecked';
  }
}

function getChildCheckState(
  parentCheckState: CheckedStateType,
  currentChildCheckState: CheckedStateType
): CheckedStateType {
  if (parentCheckState === 'checked') {
    return 'checked-by-parent';
  } else if (parentCheckState === 'indeterminate') {
    return currentChildCheckState === 'checked-by-parent' ? 'checked' : currentChildCheckState;
  }
  return parentCheckState;
}
